import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { formatNumber } from '@/utils/formatNumber';
import { debug } from '@/utils/debug';

export function BatchEmbeddingProcessor({ organizationId }: { organizationId: string }) {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(0);
  const { toast } = useToast();

  const processPhotos = async () => {
    try {
      setProcessing(true);
      
      debug.log('Starting batch processing for org:', organizationId);
      
      // Get only photos without embeddings in this org
      const { data: photos, error } = await supabase
        .from('photos')
        .select('id, url, filename, embedding')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .is('embedding', null);

      debug.log('Query result:', { photos: photos?.length, error });

      if (error) {
        console.error('Query error:', error);
        throw error;
      }
      
      if (!photos || photos.length === 0) {
        console.warn('No photos found');
        toast({
          title: 'No photos found',
          description: 'Upload some photos first',
        });
        return;
      }

      // Filter to only photos without embeddings
      const photosToProcess = photos.filter(p => !p.embedding);
      
      debug.log(`Found ${photos.length} total photos, ${photosToProcess.length} need embeddings`);
      
      if (photosToProcess.length === 0) {
        toast({
          title: 'No photos to process',
          description: 'All photos already have embeddings',
        });
        return;
      }

      setTotal(photosToProcess.length);
      debug.log(`Processing ${photosToProcess.length} photos...`);

      let successful = 0;
      let failed = 0;

      for (let i = 0; i < photosToProcess.length; i++) {
        const photo = photosToProcess[i];
        setCurrent(i + 1);
        setProgress(((i + 1) / photosToProcess.length) * 100);

        try {
          debug.log(`[${i + 1}/${photosToProcess.length}] Generating embedding for ${photo.filename}`);
          
          const { data: result, error: invokeError } = await supabase.functions.invoke(
            'generate-embedding',
            { body: { photoId: photo.id, imageUrl: photo.url } }
          );

          if (invokeError || !result?.success) {
            throw invokeError || new Error('Embedding generation failed');
          }
          
          successful++;
          debug.log(`[${i + 1}/${photosToProcess.length}] ✓ ${photo.filename}`);
        } catch (error) {
          failed++;
          console.error(`[${i + 1}/${photosToProcess.length}] ✗ ${photo.filename}:`, error);
        }

        // Small delay to avoid overwhelming browser
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast({
        title: 'Batch processing complete',
        description: `Processed ${formatNumber(successful)} photos successfully, ${formatNumber(failed)} failed`,
      });

    } catch (error) {
      console.error('Batch processing error:', error);
      toast({
        title: 'Processing error',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
      setProgress(0);
      setCurrent(0);
      setTotal(0);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2">Batch Generate Embeddings</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Generate CLIP embeddings for photos that don't have them yet
        </p>
      </div>

      {processing && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Processing {formatNumber(current)} of {formatNumber(total)}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} />
        </div>
      )}

      <Button 
        onClick={processPhotos} 
        disabled={processing}
      >
        {processing ? 'Processing...' : 'Start Batch Processing'}
      </Button>
    </div>
  );
}
